import { SystemStyleInterpolation } from "@chakra-ui/styled-system";

import { DISABLED_OPACITY } from "../consts/other-consts";

export const createButtonVariant = <T extends SystemStyleInterpolation>(overrides: T) => {
  const overrideEntries = Object.entries(overrides);
  // Styles like background, color, borderColor, etc. that do not have !important in the value
  // will be overridden with !important in the disabled state to ensure that the disabled styles are applied correctly
  const disabledStyles = overrideEntries.length
    ? Object.fromEntries(
        overrideEntries
          .filter(
            ([_key, value]) =>
              (typeof value === "string" || typeof value === "number") &&
              !value.toString().includes("!important")
          )
          .map(([key, value]) => [key, `${value} !important`])
      )
    : {};

  return {
    h: "button",
    px: 4,
    minW: "button",
    fontWeight: "600",
    rounded: "base",
    overflow: "hidden",
    fontFamily: "Roboto, sans-serif",
    ...overrides,
    _disabled: {
      ...("_disabled" in overrides ? overrides._disabled : {}),
      ...disabledStyles,
      opacity: DISABLED_OPACITY,
      cursor: "not-allowed",
    },
  };
};

export const BUTTON_STYLES = {
  baseStyle: createButtonVariant({}),
  variants: {
    ghost: {
      fontWeight: "400",
      color: "brand.deep_sea",
    },
    primary: createButtonVariant({
      bg: "button.primary",
      color: "gray.100",
      borderColor: "button.primary",
      _hover: {
        bg: "brand.analytic_hover",
      },
    }),
    secondary: createButtonVariant({
      bg: "brand.glacier",
      color: "#316489",
      borderColor: "brand.border",
      borderWidth: "2px",
      _hover: {
        bg: "#CBD4D9",
      },
    }),
    tertiary: createButtonVariant({
      bg: "transparent",
      color: "brand.analytic",
      borderColor: "brand.border",
      borderWidth: "1px",
      _active: {
        bg: "brand.clear_creek",
        color: "white !important",
        _hover: {
          color: "brand.analytic !important",
        },
      },
      _hover: {
        bg: "button.tertiary.hover",
      },
    }),
    fourthiary: createButtonVariant({
      h: "fit-content",
      p: 0,
      bg: "fff",
      color: "brand.analytic",
      borderColor: "brand.border",
      borderWidth: "0px",
      _hover: {
        color: "brand.analytic_hover",
      },
    }),
    listitem: {
      p: 2,
      whiteSpace: "normal",
      textAlign: "left",
      display: "block",
      h: "fit-content",
      transition: "background-color 300ms",
      bg: "brand.cloudy",
      borderWidth: "1px",
      rounded: "base",
      _hover: {
        bg: "semantic.info.background",
        transform: "scale(1.01)",
      },
    },
    danger: createButtonVariant({
      bg: "red.500",
      color: "gray.100",
      borderWidth: "2px",
      borderColor: "red.500",
      _hover: {
        bg: "linear-gradient(0deg, rgba(44, 59, 69, 0.10) 0%, rgba(44, 59, 69, 0.10) 100%), #E94E3D",
      },
    }),
  },
};
